import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { AllocationPieChart } from '@/components/charts/AllocationPieChart';
import { AllocationExplorer } from '@/components/charts/AllocationExplorer';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PieChart, BarChart3, Filter } from 'lucide-react';

export const DistributionSection = () => {
  const [selectedAllocation, setSelectedAllocation] = useState(null);
  
  return (
    <section id="distribution" className="py-24 relative">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-card/30 to-background" />
      <div className="absolute top-0 left-0 right-0 h-px neural-line" />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="text-primary text-sm font-medium tracking-wider uppercase">
            Distribution
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mt-3 mb-4">
            Token Allocation Breakdown
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Explore the complete distribution of $NEURO tokens across different categories.
            Click on any segment to see detailed information.
          </p>
        </motion.div>
        
        {/* Tabs */}
        <Tabs defaultValue="pie" className="w-full">
          <div className="flex justify-center mb-8">
            <TabsList className="glass">
              <TabsTrigger value="pie" className="flex items-center gap-2">
                <PieChart className="w-4 h-4" />
                Pie Chart
              </TabsTrigger>
              <TabsTrigger value="explorer" className="flex items-center gap-2">
                <Filter className="w-4 h-4" />
                Explorer
              </TabsTrigger>
            </TabsList>
          </div>
          
          <TabsContent value="pie">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass rounded-2xl p-6 md:p-8"
            >
              <AllocationPieChart 
                onAllocationSelect={setSelectedAllocation}
                selectedAllocation={selectedAllocation}
              />
            </motion.div>
          </TabsContent>
          
          <TabsContent value="explorer">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <AllocationExplorer />
            </motion.div>
          </TabsContent>
        </Tabs>
        
        {/* Quick Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12"
        >
          <div className="glass rounded-xl p-4 text-center">
            <p className="text-xs text-muted-foreground mb-1">Community Focused</p>
            <p className="text-2xl font-bold text-accent">50%</p>
            <p className="text-xs text-muted-foreground">Liquidity + Community + Marketing</p>
          </div>
          <div className="glass rounded-xl p-4 text-center">
            <p className="text-xs text-muted-foreground mb-1">Foundation Aligned</p>
            <p className="text-2xl font-bold text-secondary">35%</p>
            <p className="text-xs text-muted-foreground">Treasury + R&D + Governance + Team</p>
          </div>
          <div className="glass rounded-xl p-4 text-center">
            <p className="text-xs text-muted-foreground mb-1">Early Backers</p>
            <p className="text-2xl font-bold" style={{ color: 'hsl(35, 80%, 50%)' }}>15%</p>
            <p className="text-xs text-muted-foreground">With cliff + vesting</p>
          </div>
          <div className="glass rounded-xl p-4 text-center">
            <p className="text-xs text-muted-foreground mb-1">Team Share</p>
            <p className="text-2xl font-bold" style={{ color: 'hsl(15, 70%, 55%)' }}>5%</p>
            <p className="text-xs text-muted-foreground">24mo cliff + 36mo vest</p>
          </div>
        </motion.div>
        
        {/* Neural line separator */}
        <div className="mt-24 neural-line" />
      </div>
    </section>
  );
};

export default DistributionSection;
